import { type CrewAssignment, servingOn } from './crew.js';
import { type ContractAssignment, crewContract } from './crew-contract.js';
import { DomainError } from './errors.js';
import { addDays, type LocalDate, parseLocalDate } from './local-date.js';

/**
 * Joining / Sign-Off kuralları (ADR 0131 §3, §4). Saf: `today` parametreyle gelir; bütün tarihler
 * gemi yerel tarihidir (`YYYY-MM-DD`; ADR 0065). Katılış atamanın `valid_from`'u, ayrılış
 * `valid_to`'sudur (bitiş günü dahil; ADR 0076 §1: aynı veri iki yerde tutulmaz).
 */

/**
 * Ayrılış nedenleri (kapalı liste): MLC 2006 Standard A2.5.1 §1 geri dönüş nedenleri (kontrat
 * bitişi, hastalık, gemi satışı, ...) ve emsal listeleri (Adonis, AMOS Crewing).
 */
export const SIGN_OFF_REASONS = [
  'end_of_contract',
  'medical',
  'compassionate',
  'crew_request',
  'company_request',
  'disciplinary',
  'transfer',
  'promotion',
  'vessel_sale_or_layup',
  'other',
] as const;
export type SignOffReason = (typeof SIGN_OFF_REASONS)[number];

/** Değiştirilemez hareket olayının türleri (`crew_movement_events.kind`). */
export const MOVEMENT_KINDS = [
  'sign_on',
  'sign_off',
  'rank_change',
  'amend',
  'cancel_joining',
  'revoke_sign_off',
] as const;
export type MovementKind = (typeof MOVEMENT_KINDS)[number];

/** En küçük çalışma yaşı [MLC 2006 Standard A1.1 §1]. */
export const MINIMUM_SEAFARER_AGE = 16;
/** İleri tarih sınırı: değişim en çok bir yıl önceden planlanır. */
export const MOVEMENT_MAX_FUTURE_DAYS = 365;
export const MOVEMENT_REMARKS_MAX = 500;
export const MOVEMENT_PORT_MAX = 120;

export type MovementStatus = 'pending' | 'completed';

/** Katılış ya da ayrılış günü bugünden sonraysa Pending, değilse Completed (tasarım 5 rozeti). */
export function movementStatus(date: LocalDate, today: LocalDate): MovementStatus {
  return date > today ? 'pending' : 'completed';
}

/** `day` günündeki tam yaş (29 Şubat doğumlu artık olmayan yılda 1 Mart'ta büyür). */
export function ageOn(dateOfBirth: LocalDate, day: LocalDate): number {
  const born = parseLocalDate(dateOfBirth);
  const at = parseLocalDate(day);
  const years = at.year - born.year;
  const birthdayPassed = at.month > born.month || (at.month === born.month && at.day >= born.day);
  return birthdayPassed ? years : years - 1;
}

function assertNotTooFar(date: LocalDate, today: LocalDate): void {
  parseLocalDate(date);
  if (date > addDays(today, MOVEMENT_MAX_FUTURE_DAYS)) throw new DomainError('DATE_TOO_FAR', date);
}

const reasonText = (value: string | null | undefined): string | null => {
  const text = value?.trim() ?? '';
  if (text.length > MOVEMENT_REMARKS_MAX) {
    throw new DomainError('VALIDATION_FAILED', 'remarks', 'remarks');
  }
  return text === '' ? null : text;
};

/** Kırpılmış gerekçe ya da not; boşsa `null`; 500'den uzunsa `VALIDATION_FAILED`. */
export function parseMovementRemarks(value: string | null | undefined): string | null {
  return reasonText(value);
}

/** Liman: serbest ad (≤ 120) ve UN/LOCODE (5 harf/rakam; Port Library, ADR 0048). */
export function parseMovementPort(input: {
  name?: string | null | undefined;
  code?: string | null | undefined;
}): { name: string | null; code: string | null } {
  const name = input.name?.trim() ?? '';
  const code = input.code?.trim().toUpperCase() ?? '';
  if (name.length > MOVEMENT_PORT_MAX) throw new DomainError('VALIDATION_FAILED', 'port', 'port');
  if (code !== '' && !/^[A-Z]{2}[A-Z0-9]{3}$/.test(code)) {
    throw new DomainError('VALIDATION_FAILED', 'portCode', 'portCode');
  }
  return { name: name === '' ? null : name, code: code === '' ? null : code };
}

/** Kişinin bir ataması (çakışma denetimi için). */
export interface MovementAssignment {
  id?: string;
  vesselId: string;
  validFrom: LocalDate;
  validTo: LocalDate | null;
}

const END = '9999-12-31';

/**
 * Aynı kişinin iki ataması aynı günü kapsayamaz (`CREW_ASSIGNMENT_OVERLAP`). Tek istisna
 * gemi değiştirme günü: farklı gemilerde birinin son günü ötekinin ilk günüyse (transfer) kişi o gün
 * iki gemidedir; bordro kişi-günü bir kez sayar (ADR 0123 §2). Aynı gemide tek gün bile çakışamaz
 * (Rank change D−1 / D bırakır). `candidate.id` verilirse kendi satırı yok sayılır.
 */
export function assertCrewOverlap(
  candidate: MovementAssignment,
  others: readonly MovementAssignment[],
): void {
  const cFrom = candidate.validFrom;
  const cTo = candidate.validTo ?? END;
  for (const other of others) {
    if (candidate.id !== undefined && other.id === candidate.id) continue;
    const oTo = other.validTo ?? END;
    if (!(cFrom <= oTo && other.validFrom <= cTo)) continue;
    const handover =
      other.vesselId !== candidate.vesselId &&
      ((other.validTo !== null && other.validTo === cFrom) ||
        (candidate.validTo !== null && candidate.validTo === other.validFrom));
    if (!handover) {
      throw new DomainError('CREW_ASSIGNMENT_OVERLAP', `${other.vesselId}/${other.validFrom}`);
    }
  }
}

export interface SignOnCheck {
  vesselId: string;
  /** Katılış (gemi yerel tarihi). */
  date: LocalDate;
  /** Ayrılış önceden biliniyorsa (Amend ya da transfer); yoksa açık. */
  validTo?: LocalDate | null;
  today: LocalDate;
  /** Personel kartı; yoksa `null`. */
  profile: { nationality: string | null; dateOfBirth: LocalDate | null } | null;
  /** Kişinin silinmemiş diğer atamaları (bütün gemiler). */
  others: readonly MovementAssignment[];
  /** Düzeltilen atamanın kimliği (Amend). */
  id?: string;
}

/**
 * Sign On (ADR 0131 §3): kartta uyruk ve doğum tarihi zorunlu (`CREW_PROFILE_INCOMPLETE`; FAL 5
 * ve yaş denetimi), katılış günü 16 yaşından küçük ret (`UNDER_MINIMUM_AGE`) [MLC 2006 Standard
 * A1.1 §1], en çok 365 gün ileri (`DATE_TOO_FAR`; geçmiş serbest: değişim sonradan kaydedilir),
 * çakışma yok (`CREW_ASSIGNMENT_OVERLAP`).
 */
export function assertSignOn(check: SignOnCheck): void {
  assertNotTooFar(check.date, check.today);
  if (check.validTo !== undefined && check.validTo !== null) {
    parseLocalDate(check.validTo);
    if (check.validTo < check.date) throw new DomainError('SIGN_OFF_BEFORE_JOINING', check.validTo);
  }
  const nationality = check.profile?.nationality ?? null;
  const dateOfBirth = check.profile?.dateOfBirth ?? null;
  if (!nationality || !dateOfBirth) throw new DomainError('CREW_PROFILE_INCOMPLETE');
  if (ageOn(dateOfBirth, check.date) < MINIMUM_SEAFARER_AGE) {
    throw new DomainError('UNDER_MINIMUM_AGE', `${dateOfBirth}/${check.date}`);
  }
  assertCrewOverlap(
    {
      ...(check.id === undefined ? {} : { id: check.id }),
      vesselId: check.vesselId,
      validFrom: check.date,
      validTo: check.validTo ?? null,
    },
    check.others,
  );
}

export interface SignOffCheck {
  validFrom: LocalDate;
  validTo: LocalDate | null;
  date: LocalDate;
  reason: string;
  remarks?: string | null;
  today: LocalDate;
}

/** Ayrılış nedeni listede mi (`VALIDATION_FAILED`); `other`'da not zorunlu (`REASON_REQUIRED`). */
export function parseSignOffReason(
  reason: string,
  remarks?: string | null,
): { reason: SignOffReason; remarks: string | null } {
  if (!(SIGN_OFF_REASONS as readonly string[]).includes(reason)) {
    throw new DomainError('VALIDATION_FAILED', 'reason', 'reason');
  }
  const note = reasonText(remarks);
  if (reason === 'other' && note === null) throw new DomainError('REASON_REQUIRED', 'remarks');
  return { reason: reason as SignOffReason, remarks: note };
}

/**
 * Sign Off: açık atamayı kapatır (zaten kapalıysa `ALREADY_SIGNED_OFF`; değiştirmek Amend'dir);
 * tarih katılıştan önce olamaz (`SIGN_OFF_BEFORE_JOINING`), en çok 365 gün ileri.
 */
export function assertSignOff(check: SignOffCheck): void {
  if (check.validTo !== null) throw new DomainError('ALREADY_SIGNED_OFF', check.validTo);
  assertNotTooFar(check.date, check.today);
  if (check.date < check.validFrom) throw new DomainError('SIGN_OFF_BEFORE_JOINING', check.date);
  parseSignOffReason(check.reason, check.remarks);
}

/**
 * Rank change (aynı gemide terfi): eski atama `D−1`'de kapanır, yeni atama `D`'de açılır ve eski
 * atamanın bitişini devralır; hizmet zinciri kesilmez (ADR 0076 §3 ardışık günler). `D` katılıştan
 * sonra (`RANK_CHANGE_BEFORE_JOINING`, ayrıntı katılış tarihi; katılıştan önce rütbe Amend ile
 * düzeltilir) ve (varsa) ayrılıştan önce ya da aynı gün olmalı (`RANK_CHANGE_AFTER_SIGN_OFF`,
 * ayrıntı ayrılış tarihi). Sahip 2026-10-31: "Tarihler sırasız." ne yapılacağını anlatmıyordu.
 */
export function rankChangePlan(
  assignment: { validFrom: LocalDate; validTo: LocalDate | null },
  date: LocalDate,
  today: LocalDate,
): { closeOn: LocalDate; openOn: LocalDate; validTo: LocalDate | null } {
  assertNotTooFar(date, today);
  if (date <= assignment.validFrom) {
    throw new DomainError('RANK_CHANGE_BEFORE_JOINING', date, assignment.validFrom);
  }
  if (assignment.validTo !== null && date > assignment.validTo) {
    throw new DomainError('RANK_CHANGE_AFTER_SIGN_OFF', date, assignment.validTo);
  }
  return { closeOn: addDays(date, -1), openOn: date, validTo: assignment.validTo };
}

/** Rank change'in geçerli günleri: en erken katılış+1, en geç (varsa) ayrılış (`rankChangePlan`). */
export function rankChangeWindow(assignment: { validFrom: LocalDate; validTo: LocalDate | null }): {
  first: LocalDate;
  last: LocalDate | null;
} {
  return { first: addDays(assignment.validFrom, 1), last: assignment.validTo };
}

/** Pencerenin varsayılan günü: bugün geçerliyse bugün, değilse katılış+1. */
export function rankChangeDefault(
  assignment: { validFrom: LocalDate; validTo: LocalDate | null },
  today: LocalDate,
): LocalDate {
  const { first, last } = rankChangeWindow(assignment);
  return today >= first && (last === null || today <= last) ? today : first;
}

/**
 * Amend ve diğer düzeltmelerin gerekçesi: katılış ya da ayrılış Completed ise zorunlu
 * (`REASON_REQUIRED`); gemide kaydı olabilir, audit izi gerekçeyle okunur.
 */
export function assertMovementReason(
  assignment: { validFrom: LocalDate; validTo: LocalDate | null; today: LocalDate },
  reason: string | null | undefined,
): string | null {
  const text = reasonText(reason);
  const completed =
    movementStatus(assignment.validFrom, assignment.today) === 'completed' ||
    (assignment.validTo !== null &&
      movementStatus(assignment.validTo, assignment.today) === 'completed');
  if (completed && text === null) throw new DomainError('REASON_REQUIRED');
  return text;
}

/**
 * Katılış iptali: yalnız Pending katılış (`valid_from > bugün`); Completed katılış silinmez
 * (`JOINING_COMPLETED`; Sign Off ya da Amend ile düzeltilir). Gerekçe her zaman zorunlu.
 */
export function assertCancelJoining(check: {
  validFrom: LocalDate;
  today: LocalDate;
  reason: string | null | undefined;
}): string {
  if (movementStatus(check.validFrom, check.today) === 'completed') {
    throw new DomainError('JOINING_COMPLETED', check.validFrom);
  }
  const text = reasonText(check.reason);
  if (text === null) throw new DomainError('REASON_REQUIRED');
  return text;
}

/** Ayrılışı geri alma: ayrılış yoksa `NOT_SIGNED_OFF`; Completed ise gerekçe zorunlu. */
export function assertRevokeSignOff(check: {
  validTo: LocalDate | null;
  today: LocalDate;
  reason?: string | null;
}): string | null {
  if (check.validTo === null) throw new DomainError('NOT_SIGNED_OFF');
  const text = reasonText(check.reason);
  if (movementStatus(check.validTo, check.today) === 'completed' && text === null) {
    throw new DomainError('REASON_REQUIRED');
  }
  return text;
}

export type ContractStatus = 'planned' | 'active' | 'overdue' | 'completed';

/**
 * Contracts sekmesinin durumu (ADR 0131 §4; tasarım 4): katılış gelecekte Planned, ayrılış günü
 * geldi Completed (ADR 0131 Ek 4; Rank change D−1 / D zinciri hariç), gemide ve kontrat ya da MLC sınırı geçmiş Overdue (ADR 0076 §3), değilse
 * Active. `all` kişinin bütün atamalarıdır (kesintisiz hizmet zinciri).
 */
export function contractStatus(
  assignment: Pick<CrewAssignment, 'validFrom' | 'validTo'>,
  all: readonly ContractAssignment[],
  today: LocalDate,
): ContractStatus {
  if (assignment.validFrom > today) return 'planned';
  if (!servingOn(assignment, all, today)) return 'completed';
  return crewContract(all, today)?.state === 'overdue' ? 'overdue' : 'active';
}
